import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLearnerHome } from '../../lib/navigation/useLearnerHome';
import {
  CHAT_PHASE_ATTR,
  isHomeAffordanceVisible,
  requiresLeaveConfirm,
} from '../../lib/navigation/chatPhase';
import { clearCoachingViewParam } from '../../lib/navigation/clearCoachingViewParam'; // #2489
import { useFabStackSlot } from '../../contexts/FabStackContext';
import { FAB_PRIORITY } from '../../lib/fabStack';
import HomeButtonView from './HomeButtonView';

const HOME_ICON = (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 11.5 12 4l9 7.5" />
    <path d="M5 10v9a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9" />
  </svg>
);

/**
 * HomeButton — persistent "go home" affordance for the scenario / chat /
 * post-debrief screens (Issue #1555).
 *
 * Thusi (Anchen pilot, prod test 2026-06-11) reported a ~5-second cost to
 * find her way back to the home grid after finishing a scenario, and asked
 * for "a home button above the report-bug button (top left)". The chat-header
 * app logo (#1518 LogoHomeLink) is the existing affordance but she found it
 * undiscoverable.
 *
 * #1696 (2026-07-27 DECIDED): this no longer renders its own fixed-position
 * FAB. It registers 'home' as a 'menu'-corner FabStack entry (icon/label/
 * onClick) — the shared FabMenu (components/fabMenu/FabMenu.jsx) renders the
 * actual button and calls back into `handleActivate` on tap. HomeButton
 * still owns all its own state (the leave-confirm dialog) and still keys
 * entirely off the single phase signal ChatPage publishes on
 * `<html data-chat-phase>` (see lib/navigation/chatPhase.js) — so it needs no
 * route allowlist, hides itself during mid-flow modals, and knows when
 * leaving needs a confirm. ACCEPTED TRADE (named, not silently assumed): Home
 * moves from one tap (the old always-visible FAB) to two taps (open the menu,
 * then tap Home) — see the #1696 decision comment's required human-test
 * point: can a learner on a phone still find their way out of a live
 * conversation with Home now behind the fan-open menu?
 *
 * Global: mounted once in App.jsx next to <BugReporter /> and <FabMenu />.
 *
 * Navigates via useLearnerHome() so it resolves to the right home today
 * (/dashboard) and automatically follows the #1518 messenger flag (/messenger)
 * when it flips — no per-callsite conditional (resolveLearnerHome contract).
 */
export default function HomeButton() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const learnerHome = useLearnerHome();
  const { t } = useTranslation();
  const [phase, setPhase] = useState(null);
  const [confirming, setConfirming] = useState(false);

  // Subscribe to ChatPage's published phase. documentElement is stable for the
  // app lifetime, so one observer suffices; read once up front to cover the
  // case where ChatPage mounted before us.
  useEffect(() => {
    const el = document.documentElement;
    const read = () => setPhase(el.getAttribute(CHAT_PHASE_ATTR));
    read();
    const obs = new MutationObserver(read);
    obs.observe(el, { attributes: true, attributeFilter: [CHAT_PHASE_ATTR] });
    return () => obs.disconnect();
  }, []);

  // Drop a stale confirm dialog if the phase changes underfoot (e.g. the run
  // completes, or navigation unmounts the chat, while "Leave?" is open).
  useEffect(() => {
    if (!requiresLeaveConfirm(phase)) setConfirming(false);
  }, [phase]);

  // Esc closes the confirm dialog.
  useEffect(() => {
    if (!confirming) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setConfirming(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [confirming]);

  const goHome = useCallback(() => {
    setConfirming(false);
    // #2489: this FAB is the COMPLETE phase's exit affordance (no confirm —
    // see requiresLeaveConfirm) and, being a global component deliberately
    // decoupled from ChatPage (see this file's header), it is not routed
    // through ChatPage's own exit handler — so it must independently clear
    // the `?view=coaching` deep-link marker (#737/#688) before leaving, or a
    // later browser-back to this run's URL silently reopens a stale debrief
    // on Act 1 (clearCoachingViewParam's header has the full mechanism;
    // observed on staging run 3574, #2489).
    clearCoachingViewParam();
    navigate(learnerHome);
  }, [navigate, learnerHome]);

  const handleActivate = useCallback(() => {
    if (requiresLeaveConfirm(phase)) setConfirming(true);
    else goHome();
  }, [phase, goHome]);

  const eligible = isAuthenticated && !user?.is_guest;
  const visible = eligible && isHomeAffordanceVisible(phase);

  const labels = {
    aria: t('homeButton.ariaLabel'),
    tooltip: t('homeButton.tooltip'),
    confirmTitle: t('homeButton.confirmTitle'),
    confirmBody: t('homeButton.confirmBody'),
    stay: t('homeButton.stay'),
    leave: t('homeButton.leave'),
  };

  // #1696: register 'home' in the shared 'menu'-corner FabStack — FabMenu
  // renders the actual button; this hook must run every render (React rules
  // of hooks), so it sits ahead of any early return and simply reports
  // `visible` as `active`, matching every other menu registrant.
  useFabStackSlot('menu', 'home', FAB_PRIORITY.HOME, visible, {
    icon: HOME_ICON,
    label: labels.tooltip,
    ariaLabel: labels.aria,
    onClick: handleActivate,
    testId: 'fab-menu-item-home',
  });

  if (!visible) return null;

  return (
    <HomeButtonView
      labels={labels}
      confirming={confirming}
      onConfirmLeave={goHome}
      onCancelLeave={() => setConfirming(false)}
    />
  );
}
